import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import SIGNUPIMAGE from '../../assets/loginimage.jpg'; // You can use a different image

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('/assets/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('/assets/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('/assets/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/assets/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans Medium', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    overflow-x: hidden;
  }
`;

const SignupContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
`;

const LeftPanel = styled.div`
  flex: 1;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 968px) {
    display: none;
  }
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  background-image: url(${SIGNUPIMAGE});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  
  /* Dark overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(2, 6, 17, 0.7) 0%, rgba(5, 11, 21, 0.8) 50%, rgba(2, 6, 17, 0.7) 100%);
    z-index: 1;
  }
  
  /* Blue accent overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(59, 130, 246, 0.2) 0%, transparent 70%);
    z-index: 2;
  }
`;

const RightPanel = styled.div`
  flex: 1.2;
  max-width: 600px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: transparent;
  overflow-y: auto;
  
  @media (max-width: 968px) {
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const Logo = styled.div`
  margin-bottom: 2rem;
  text-align: center;
  
  h1 {
    font-size: 2rem;
    font-weight: 600;
    color: #ffffff;
    font-family: 'Very Sans Bold', sans-serif;
    margin-bottom: 0.5rem;
  }
`;

const SignupForm = styled.form`
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  text-align: left;
  margin-bottom: 1.5rem;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
`;

const SubTitle = styled.p`
  text-align: left;
  margin-bottom: 2rem;
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.95rem;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.25rem;
  
  @media (max-width: 568px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #94A3B8;
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.95rem;
  font-family: 'Very Sans Light', sans-serif;
  color: #ffffff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  color: #94A3B8;
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.85rem;
  cursor: pointer;
  line-height: 1.4;
  
  input {
    margin-right: 0.75rem;
    margin-top: 0.2rem;
    transform: scale(1.1);
    accent-color: #3B82F6;
    flex-shrink: 0;
    cursor: pointer;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'Very Sans Bold', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 1rem 0;
  
  &:hover {
    background: #2563EB;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  text-align: center;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.9rem;
  position: absolute;
  top: 50px;
  z-index: 50;
  max-width: 480px;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 6px;
  text-align: center;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.9rem;
  position: absolute;
  top: 50px;
  z-index: 50;
  max-width: 480px;
`;

const LinkText = styled.p`
  text-align: center;
  margin-top: 1rem;
  font-family: 'Very Sans Medium', sans-serif;
  color: #94A3B8;
  font-size: 0.9rem;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
  color: #94A3B8;
  font-size: 0.8rem;
  font-family: 'Very Sans Light', sans-serif;
  
  a {
    color: #3B82F6;
    text-decoration: none;
    margin: 0 0.5rem;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  padding: 3rem 2rem;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5);
  text-align: center;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-50px) scale(0.9);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media screen and (max-width: 768px) {
  padding: 2rem 1rem;
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #3B82F6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: white;
`;

const ModalTitle = styled.h3`
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
`;

const ModalMessage = styled.p`
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 1rem;
  line-height: 1.5;
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -20%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    overflow: hidden;
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

const GradientOrb2 = styled.div`
    position: absolute;
    bottom: 20%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

export default function SignupPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    dateOfBirth: '',
    newsletter: false,
    marketing: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');

    const result = await signup(formData);
    
    if (result.success) {
      if (result.autoLogin) {
        // Show success modal first
        setShowSuccessModal(true);
        
        // Navigate after 2 seconds
        setTimeout(() => {
          navigate('/virtualcard');
        }, 2000);
      } else {
        setSuccess(result.message);
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <>
      <GlobalStyle />
      <SignupContainer>
        <GradientOrb1 />
        <GradientOrb2 />
        <LeftPanel>
          <ImagePlaceholder />
        </LeftPanel>
        
        <RightPanel>
          <Logo>
            <h1>Hydrixpay</h1>
          </Logo>
          
          <SignupForm onSubmit={handleSubmit}>
            <Title>Create Account</Title>
            <SubTitle>
              Already have an account? <Link to="/login">Sign In Now</Link>
            </SubTitle>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </FormGroup>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Phone (optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <Input
                  id="dateOfBirth"
                  type="text"
                  placeholder="MM/DD/YYYY (optional)"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                />
              </FormGroup>
            </FormRow>
            
            <CheckboxContainer>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.newsletter}
                  onChange={(e) => setFormData({...formData, newsletter: e.target.checked})}
                />
                I would like to receive newsletters and updates about nomafi products and services.
              </CheckboxLabel>
            </CheckboxContainer>
            
            <CheckboxContainer>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.marketing}
                  onChange={(e) => setFormData({...formData, marketing: e.target.checked})}
                />
                I agree to receive marketing communications and promotional offers from nomafi.
              </CheckboxLabel>
            </CheckboxContainer>
            
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </SignupForm>
        </RightPanel>
      </SignupContainer>
      
      {showSuccessModal && (
        <SuccessModal>
          <ModalContent>
            <SuccessIcon>✓</SuccessIcon>
            <ModalTitle>Account Created Successfully!</ModalTitle>
            <ModalMessage>
              Welcome to HydrixPay! Redirecting you to your dashboard...
            </ModalMessage>
          </ModalContent>
        </SuccessModal>
      )}
    </>
  );
}